import {
  DEFAULT_EDUCATION_POLICY,
  EDUCATION_POLICIES,
  LEGACY_DASHBOARD_GUIDE_KEY,
} from "../shared/types";
import {
  logExperiencePlanOutput,
  logExperiencePlannerInput,
} from "../shared/pepBootDebug";

function hasLegacyFirstTimeGuidePending() {
  if (typeof window === "undefined") {
    return false;
  }

  return !window.localStorage.getItem(LEGACY_DASHBOARD_GUIDE_KEY);
}

function getEducationPolicy(releaseMeta) {
  return releaseMeta?.educationPolicy || DEFAULT_EDUCATION_POLICY;
}

function isReleaseCompleted(state, releaseId) {
  return state.releases[releaseId]?.status === "completed";
}

function hasUserOpenedWhatsNew(state, releaseId) {
  const record = state.releases[releaseId];
  return Boolean(
    record?.whatsNewOpened ||
      record?.briefCompleted ||
      record?.status === "completed",
  );
}

function buildBriefUi(policy, { isManual = false } = {}) {
  if (isManual || policy === EDUCATION_POLICIES.OPTIONAL) {
    return {
      mode: "optional",
      showContinue: false,
      showShowMe: true,
      showJustTellMe: true,
      showSkip: true,
      allowHardwareBackDismiss: true,
    };
  }

  if (policy === EDUCATION_POLICIES.MANDATORY) {
    return {
      mode: "mandatory",
      showContinue: true,
      showShowMe: false,
      showJustTellMe: false,
      showSkip: false,
      allowHardwareBackDismiss: false,
      closingPrompt: "When you're ready, press Continue.",
    };
  }

  if (policy === EDUCATION_POLICIES.SUMMARY) {
    return {
      mode: "summary",
      showContinue: false,
      showShowMe: false,
      showJustTellMe: true,
      showSkip: true,
      allowHardwareBackDismiss: true,
    };
  }

  return {
    mode: "optional",
    showContinue: false,
    showShowMe: true,
    showJustTellMe: true,
    showSkip: true,
    allowHardwareBackDismiss: true,
  };
}

/**
 * Plans release education from release metadata policy and user state.
 */
export function planReleaseExperience(input) {
  logExperiencePlannerInput(input);
  const plan = planReleaseExperienceCore(input);
  logExperiencePlanOutput(plan);
  return plan;
}

function planReleaseExperienceCore({
  detection,
  state,
  isGuest = false,
  context = "auto",
}) {
  const { currentReleaseId, isNewRelease, tourWorthyFeatures, releaseMeta } =
    detection;
  const settings = state.settings;
  const releaseRecord = state.releases[currentReleaseId] || null;
  const policy = getEducationPolicy(releaseMeta);
  const isManual = context === "manual";
  const tourFeatureIds = tourWorthyFeatures.map((feature) => feature.id);

  const basePlan = {
    releaseId: currentReleaseId,
    educationPolicy: policy,
    context,
    showBrief: false,
    showRequiredTour: false,
    briefVariant: "full",
    briefUi: buildBriefUi(policy, { isManual }),
    defaultChoice: null,
    autoStartTour: false,
    requiredTour: false,
    tourFeatureIds,
    blockDashboard: false,
    allowHardwareBackDismiss: true,
    showWhatsNewChip: false,
    narrationEnabled: Boolean(settings.ashleyNarrationEnabled),
    reason: "no_action",
  };

  const completed = isReleaseCompleted(state, currentReleaseId);
  const mustCompleteMandatory =
    policy === EDUCATION_POLICIES.MANDATORY && !completed && !isManual;

  if (mustCompleteMandatory) {
    if (releaseRecord?.briefCompleted && releaseRecord?.status === "in_progress") {
      return {
        ...basePlan,
        educationPolicy: EDUCATION_POLICIES.MANDATORY,
        showRequiredTour: true,
        requiredTour: true,
        blockDashboard: true,
        allowHardwareBackDismiss: false,
        reason: "mandatory_resume_tour",
      };
    }

    return {
      ...basePlan,
      educationPolicy: EDUCATION_POLICIES.MANDATORY,
      showBrief: true,
      requiredTour: true,
      blockDashboard: true,
      allowHardwareBackDismiss: false,
      briefUi: buildBriefUi(EDUCATION_POLICIES.MANDATORY),
      reason: "mandatory_release",
    };
  }

  if (policy === EDUCATION_POLICIES.SILENT && !isNewRelease) {
    return {
      ...basePlan,
      showWhatsNewChip: Boolean(
        settings.alwaysShowWhatsNew &&
          !hasUserOpenedWhatsNew(state, currentReleaseId),
      ),
      reason: "silent_release",
    };
  }

  if (!isNewRelease || !releaseMeta) {
    return {
      ...basePlan,
      showWhatsNewChip: Boolean(
        settings.alwaysShowWhatsNew &&
          !hasUserOpenedWhatsNew(state, currentReleaseId),
      ),
      reason: "release_already_seen",
    };
  }

  if (policy === EDUCATION_POLICIES.SILENT) {
    return {
      ...basePlan,
      showWhatsNewChip: Boolean(settings.alwaysShowWhatsNew),
      reason: "silent_new_release",
    };
  }

  if (!isManual && state.optedOutReleases.includes(currentReleaseId)) {
    return {
      ...basePlan,
      showWhatsNewChip: Boolean(settings.alwaysShowWhatsNew),
      reason: "release_opted_out",
    };
  }

  if (!isManual && !settings.autoTourEnabled) {
    return {
      ...basePlan,
      showWhatsNewChip: Boolean(settings.alwaysShowWhatsNew),
      reason: "auto_tour_disabled",
    };
  }

  if (
    !isManual &&
    hasLegacyFirstTimeGuidePending() &&
    settings.autoFirstTimeGuideEnabled &&
    policy !== EDUCATION_POLICIES.MANDATORY
  ) {
    return {
      ...basePlan,
      reason: "first_time_guide_priority",
    };
  }

  if (completed) {
    return {
      ...basePlan,
      showWhatsNewChip: Boolean(settings.alwaysShowWhatsNew),
      reason: "release_already_completed",
    };
  }

  if (releaseRecord?.status === "in_progress") {
    return {
      ...basePlan,
      showBrief: isManual || Boolean(settings.ashleyBriefEnabled),
      briefVariant: "resume",
      defaultChoice: "show_me",
      briefUi: buildBriefUi(policy, { isManual }),
      reason: "resume_in_progress",
    };
  }

  if (policy === EDUCATION_POLICIES.SUMMARY) {
    return {
      ...basePlan,
      showBrief: isManual || Boolean(settings.ashleyBriefEnabled),
      defaultChoice: "just_tell_me",
      briefUi: buildBriefUi(EDUCATION_POLICIES.SUMMARY, { isManual }),
      reason: "summary_release",
    };
  }

  if (!isManual && settings.preferSummaries && policy === EDUCATION_POLICIES.OPTIONAL) {
    return {
      ...basePlan,
      showBrief: Boolean(settings.ashleyBriefEnabled),
      defaultChoice: "just_tell_me",
      briefUi: buildBriefUi(EDUCATION_POLICIES.OPTIONAL, { isManual }),
      reason: "prefer_summaries",
    };
  }

  if (isGuest) {
    return {
      ...basePlan,
      showBrief: isManual || Boolean(settings.ashleyBriefEnabled),
      defaultChoice: "show_me",
      briefUi: buildBriefUi(policy, { isManual }),
      reason: "guest_release_brief",
    };
  }

  return {
    ...basePlan,
    showBrief: isManual || Boolean(settings.ashleyBriefEnabled),
    defaultChoice: "show_me",
    briefUi: buildBriefUi(policy, { isManual }),
    reason: "new_release_brief",
  };
}

export function isDashboardBlockedByPlan(plan) {
  return Boolean(plan?.blockDashboard);
}
